/* Shopify Sense mobile sticky ATC v2 - reuses the real product form/button. */
(() => {
  'use strict';
  if (window.__senseStickyMobileATCLoaded) return;
  window.__senseStickyMobileATCLoaded = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const initialized = new WeakSet();

  function getProductRoot(bar) {
    const id = bar.dataset.sectionId;
    return document.getElementById(`MainProduct-${id}`)
      || Array.from(document.querySelectorAll('product-info')).find(el => el.dataset.section === id)
      || bar.closest('.shopify-section')?.querySelector('product-info')
      || bar.closest('.shopify-section')?.querySelector('.product__info-wrapper')
      || null;
  }

  function init(bar) {
    if (initialized.has(bar)) return;
    const section = getProductRoot(bar);
    if (!section) {
      // Sections can be inserted asynchronously by the Shopify theme editor.
      return;
    }

    const id = bar.dataset.sectionId;
    const stickyButton = bar.querySelector('[data-sticky-button]');
    const buttonLabel = bar.querySelector('[data-sticky-button-label]');
    const priceLabel = bar.querySelector('[data-sticky-price]');
    const variantLabel = bar.querySelector('[data-sticky-variant]');
    const stickyImage = bar.querySelector('.sense-sticky-atc__image');
    if (!stickyButton || !buttonLabel || !priceLabel) return;

    initialized.add(bar);
    let pending = false;

    // Sense installations may have customized buy-buttons. Prefer the button
    // in the real product form, never a Shop Pay / dynamic checkout button.
    function getNativeButton() {
      return section.querySelector(`#ProductSubmitButton-${id}`)
        || section.querySelector(`form[id="product-form-${id}"] button[name="add"]`)
        || section.querySelector(`form[id="product-form-${id}"] button[type="submit"]`)
        || section.querySelector(`button[form="product-form-${id}"][name="add"]`)
        || section.querySelector('product-form button.product-form__submit')
        || section.querySelector('form[action*="/cart/add"] button[name="add"]')
        || section.querySelector('button.product-form__submit')
        || null;
    }

    function getForm() {
      const nativeButton = getNativeButton();
      return section.querySelector(`form[id="product-form-${id}"]`)
        || nativeButton?.form
        || section.querySelector('product-form form[data-type="add-to-cart-form"]')
        || section.querySelector('product-form form[action*="/cart/add"]')
        || null;
    }

    let legacyScript = null;
    let legacyVariants = null;
    function getSelectedVariant() {
      const selected = section.querySelector('variant-selects [data-selected-variant]');
      if (selected) {
        try { return JSON.parse(selected.textContent); } catch (_) { /* fallback */ }
      }
      const script = section.querySelector(
        'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
      );
      if (!script) return null;
      if (script !== legacyScript) {
        legacyScript = script;
        try { legacyVariants = JSON.parse(script.textContent); }
        catch (_) { legacyVariants = null; }
      }
      const selectedId = getForm()?.querySelector('input[name="id"]')?.value;
      return Array.isArray(legacyVariants)
        ? legacyVariants.find(variant => String(variant.id) === String(selectedId)) || null
        : null;
    }

    function syncContent() {
      const nativeButton = getNativeButton();
      const form = getForm();
      const input = form?.querySelector('input[name="id"]');
      const variantId = input?.value;
      const unavailable = !!input && !variantId;
      const disabled = !nativeButton || nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true'
        || !!input?.disabled || unavailable;
      stickyButton.disabled = disabled;

      const nativeText = nativeButton?.querySelector('span')?.textContent?.trim();
      buttonLabel.textContent = unavailable ? (bar.dataset.unavailableLabel || 'Unavailable')
        : disabled ? (nativeText || bar.dataset.soldOutLabel || 'Sold out')
        : (bar.dataset.addLabel || 'Add to cart');

      const priceRoot = section.querySelector(`[id="price-${id}"]`);
      if (priceRoot?.classList.contains('hidden')) {
        priceLabel.textContent = '\u2014';
      } else if (priceRoot) {
        const priceBox = priceRoot.querySelector('.price');
        const price = priceBox?.classList.contains('price--on-sale')
          ? priceRoot.querySelector('.price__sale .price-item--sale')
          : priceRoot.querySelector('.price__regular .price-item--regular');
        if (price) priceLabel.textContent = price.textContent.trim();
      }

      const variant = getSelectedVariant();
      if (variantLabel) {
        let title = variant?.title || '';
        if (!title) {
          const fields = Array.from(section.querySelectorAll(
            'variant-selects fieldset input:checked, variant-selects select, variant-radios fieldset input:checked'
          ));
          title = fields.map(field => field.value).filter(Boolean).join(' / ');
        }
        variantLabel.textContent = title;
        variantLabel.hidden = !title || title === 'Default Title';
      }

      const mediaId = variant?.featured_media?.id;
      if (stickyImage && mediaId) {
        const media = section.querySelector(`media-gallery [data-media-id$="-${mediaId}"] img`);
        const src = media?.currentSrc || media?.src;
        if (src && stickyImage.src !== src) stickyImage.src = src;
      }
    }

    function syncVisibility() {
      const button = getNativeButton();
      const pastButton = button && button.getBoundingClientRect().bottom < 0;
      const show = Boolean(mobileQuery.matches && pastButton);
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('sense-sticky-atc-open', show);
      if (show) {
        document.body.style.setProperty('--sense-sticky-atc-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`);
      }
    }

    function refresh() {
      syncContent();
      syncVisibility();
    }

    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        if (bar.isConnected && section.isConnected) refresh();
      });
    }

    stickyButton.addEventListener('click', () => {
      const button = getNativeButton();
      if (!stickyButton.disabled && button && !button.disabled) button.click();
    });

    section.addEventListener('change', schedule);
    section.addEventListener('input', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(section, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled', 'src', 'value']
    });

    window.addEventListener('scroll', syncVisibility, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', schedule);
    refresh();
  }

  function initAll() {
    document.querySelectorAll('[data-sense-sticky-atc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
